/**
 * The seven tabs, their order, and the one thing about them that is not cosmetic: six keys are
 * mobile's, and `forma` (the video analysis) is a web tab mobile does not have.
 *
 * `?tab=` uses the same six words mobile's own `?tab=` does — `overview | calendar | progress |
 * body | records | surveys` — so a deep link written on one platform opens the same tab on the
 * other (handoff B1). `progress` in particular keeps its key while its LABEL reads "Trendy": mobile
 * renamed the value, not the key, in PR #446, and renaming the key here would break every link
 * either app has ever produced.
 *
 * The default is `overview` — owner decision J1, 10 August 2026. The 6 August rule that a client
 * row led straight to the calendar is superseded by it, and the summary column's plan card is the
 * one click that gets the old shortcut back.
 */

import { tabFocusTarget as stripFocusTarget } from '~/shared/ui/TabStrip';

export const CLIENT_DETAIL_TABS = [
  'overview',
  'calendar',
  'surveys',
  'body',
  'forma',
  'progress',
  'records',
  'nutrition',
] as const;

export type ClientDetailTab = (typeof CLIENT_DETAIL_TABS)[number];

export const DEFAULT_CLIENT_DETAIL_TAB: ClientDetailTab = 'overview';

export function isClientDetailTab(value: unknown): value is ClientDetailTab {
  return (
    typeof value === 'string' && (CLIENT_DETAIL_TABS as readonly string[]).includes(value)
  );
}

/**
 * The label each tab wears.
 *
 * Five come from `clients.detail.tabs.*` — the verbatim copy of mobile's locale, which is the whole
 * mechanism by which a trainer recognises the two apps as one product.
 *
 * `progress` does NOT, and the exception is recorded rather than hidden: this repo's copy of
 * `pl.json` still reads "Postępy" where mobile now reads "Trendy" and where the canon draws
 * "Trendy". `src/shared/locales/{pl,en}.json` are never edited here — a re-sync overwrites them —
 * so the current value is overridden from the `web:` namespace until that re-sync happens, and the
 * key it overrides is named in the override's own comment. Delete the override, not the key, when
 * the copy catches up.
 */
export function clientDetailTabLabel(
  tab: ClientDetailTab,
  t: (key: string) => string,
): string {
  // `forma` is a web tab mobile does not have, so its word is the web namespace's own. `nutrition`
  // shares mobile's key, and its word lives here until the mobile copy is re-synced.
  if (tab === 'progress' || tab === 'forma' || tab === 'nutrition') {
    return t(`web:clientDetail.tabs.${tab}`);
  }
  return t(`clients.detail.tabs.${tab}`);
}

/**
 * Where a key press moves FOCUS inside the tab strip, in this screen's own six keys.
 *
 * The RULE is `shared/ui/TabStrip`'s and is documented there — arrows and Home/End move focus,
 * Enter and Space are left to the browser, and the ends wrap. What lives here is the ORDER it walks,
 * because `CLIENT_DETAIL_TABS` is this slice's declaration of which tabs exist and in which order,
 * and the strip takes that as a parameter rather than knowing about any screen.
 */
export function tabFocusTarget(
  key: string,
  current: ClientDetailTab,
  tabs: readonly ClientDetailTab[] = CLIENT_DETAIL_TABS,
): ClientDetailTab | null {
  return stripFocusTarget(key, current, tabs);
}

/**
 * The tabs this trainer is shown for this client. Odżywianie stands only where nutrition R16 opens
 * the module: a trainer on the pilot, or a client who already has a target
 * (`lib/useNutrition`'s `useNutritionTabStands`). Every other tab always stands.
 */
export function visibleClientDetailTabs(nutritionStands: boolean): readonly ClientDetailTab[] {
  return nutritionStands ? CLIENT_DETAIL_TABS : CLIENT_DETAIL_TABS.filter((tab) => tab !== 'nutrition');
}
